import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Language } from '../types';

interface SettingsProps {
  language: Language;
  onClose: () => void;
}

interface Classroom {
  id: string;
  name: string;
}

const Settings: React.FC<SettingsProps> = ({ language, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    const init = async () => {
      const sessionResult = await supabase.auth.getSession();
      const user = sessionResult.data.session?.user;

      if (!user) {
        console.warn("⚠️ No active session - user not logged in");
        return;
      }

      await loadUserProfile();
      await loadClassrooms();
    };

    init();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, role')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setUserRole(data.role || '');

        if (data.role === 'student') {
          const { data: studentClass } = await supabase
            .from('classroom_students')
            .select('classroom_id')
            .eq('student_id', user.id)
            .maybeSingle();

          if (studentClass) {
            setSelectedClassroom(studentClass.classroom_id);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadClassrooms = async () => {
    try {
      const { data, error } = await supabase
        .from('classrooms')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setClassrooms(data || []);
    } catch (error) {
      console.error('Error loading classrooms:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          updated_at: new Date().toISOString(),
        });

      if (profileError) throw profileError;

      // Update classroom assignment if student
      if (userRole === 'student') {
        // Remove from current classroom if any
        await supabase
          .from('classroom_students')
          .delete()
          .eq('student_id', user.id);

        // Add to new classroom
        if (selectedClassroom !== 'none') {
          const { error: classroomError } = await supabase
            .from('classroom_students')
            .insert({
              classroom_id: selectedClassroom,
              student_id: user.id,
            });

          if (classroomError) throw classroomError;
        }
      }

      setMessage({
        text: language === 'en' ? 'Profile updated successfully!' : 'Profil mis à jour avec succès !',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({
        text: language === 'en' ? 'Error updating profile' : 'Erreur lors de la mise à jour du profil',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {language === 'en' ? 'Profile Settings' : 'Paramètres du Profil'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {language === 'en' ? 'First Name' : 'Prénom'}
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                  text-slate-900 dark:text-slate-100 rounded-lg 
                  focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {language === 'en' ? 'Last Name' : 'Nom'}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                  text-slate-900 dark:text-slate-100 rounded-lg 
                  focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
              />
            </div>

            {userRole === 'student' && (
              <div>
                <label htmlFor="classroom" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === 'en' ? 'Classroom' : 'Classe'}
                </label>
                <select
                  id="classroom"
                  value={selectedClassroom}
                  onChange={(e) => setSelectedClassroom(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                    text-slate-900 dark:text-slate-100 rounded-lg 
                    focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400"
                >
                  <option value="none">
                    {language === 'en' ? '-- Select a classroom --' : '-- Sélectionner une classe --'}
                  </option>
                  {classrooms.map((classroom) => (
                    <option key={classroom.id} value={classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {message && (
              <div className={`text-sm ${
                message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {message.text}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 dark:bg-indigo-500 text-white py-2 px-4 rounded-lg 
                hover:bg-indigo-700 dark:hover:bg-indigo-600 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? (language === 'en' ? 'Saving...' : 'Enregistrement...')
                : (language === 'en' ? 'Save Changes' : 'Enregistrer')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;